import { Logger } from '@shared/services/logger.service';
import { handleAsync } from '@host/utils/handleAsync';
import { useMeetingStore } from '@host/stores/meeting.store';
import { useNotificationStore } from '@host/stores/notification.store';
import { useWorkplaceStore } from '@host/stores/workplace.store';
import { AuditoriumRole } from '../types';
import { fetchMyAuditoriumRole } from './auditorium-moderation.service';
import { clearSpeakerApprovals } from './auditorium-speaker-approval';

/**
 * Target-side half of an auditorium promotion (NGE-1078). The Jitsi JWT carries the
 * role and is minted at join, so a new role row changes nothing in the live call —
 * the promoted client has to re-fetch `GET /:pid/token` and reconnect.
 *
 * Two things wake this up:
 *  - the promoting moderator's endpoint message (`auditorium:role-changed`), and
 *  - a ~15s poll of `GET /:pid/me/role`, the fallback for anyone who missed it.
 *
 * TODO(S6): drop both once `AuditoriumRoleChangedEvent` lands in nexgen-types and
 * the socket event can drive this directly.
 */

const ROLE_POLL_INTERVAL_MS = 15000;

export const AUDITORIUM_ROLE_CHANGED_EVENT = 'auditorium:role-changed';

let pollTimer: ReturnType<typeof setInterval> | null = null;
let syncedSessionPid: string | null = null;
/** Guards against a poll tick and an endpoint message both re-joining at once. */
let rejoinInFlight = false;

/** The auditorium's room pid — a member reads it off the current room, a guest off
 * the share-link context stashed at bootstrap. */
const resolveAuditoriumRoomPid = (): string | null => {
  const currentRoom = useWorkplaceStore.getState().currentRoom;
  if (currentRoom?.type === 'auditorium' && currentRoom.pid) return currentRoom.pid;
  try {
    const stashed = JSON.parse(sessionStorage.getItem('auditoriumContext') || 'null');
    return stashed?.roomPid ?? null;
  } catch {
    return null;
  }
};

const announceRole = (role: AuditoriumRole) => {
  const { showSnackbar } = useNotificationStore.getState();
  if (role === AuditoriumRole.AUDIENCE) {
    showSnackbar(
      'You are back in the audience — your microphone and camera stay with you, screen share does not.',
      'info'
    );
    return;
  }
  showSnackbar(
    role === AuditoriumRole.MODERATOR
      ? 'You are now a moderator — you can speak and manage the auditorium.'
      : 'You are now a speaker — your microphone, camera and screen share are available.',
    'success'
  );
};

/**
 * Re-join the auditorium so the conference is re-established with a token minted for
 * the new role. The connection is torn down first: the JWT is bound to it, so
 * re-joining the same room on the old connection would keep the old role.
 */
const rejoinWithNewRole = async (sessionPid: string, role: AuditoriumRole) => {
  if (rejoinInFlight) return;
  const roomPid = resolveAuditoriumRoomPid();
  if (!roomPid) {
    Logger.warn('[Auditorium] Role changed but no auditorium room pid to re-join');
    return;
  }
  rejoinInFlight = true;
  announceRole(role);
  try {
    const { joinJitsiMeeting, leaveJitsiMeeting } =
      await import('@host/stores/utils/meeting-store-utils');
    await leaveJitsiMeeting({ leaveReason: 'auditorium-role-changed' });
    await joinJitsiMeeting(roomPid, { auditorium: true, auditoriumSessionPid: sessionPid });
  } catch (error) {
    Logger.error('[Auditorium] Failed to re-join after a role change:', error);
  } finally {
    rejoinInFlight = false;
  }
};

/** Compare the server's view of our role with the one our token was minted for. */
const checkMyRole = async (sessionPid: string) => {
  const [err, result] = await handleAsync(() => fetchMyAuditoriumRole(sessionPid));
  if (err || !result?.role) return;
  // Ownership can change without the role changing: promoting a member to moderator
  // writes the explicit row that makes the session theirs to end, but they already
  // read as `moderator` by default, so the role comparison below would see nothing.
  // No re-join needed for this — the JWT does not carry it.
  const canModerate = result.can_moderate === undefined ? null : !!result.can_moderate;
  if (useMeetingStore.getState().auditoriumCanModerate !== canModerate) {
    useMeetingStore.setState({ auditoriumCanModerate: canModerate });
  }
  // Room moderation can also be handed over or taken back without the role changing —
  // and for a promoted guest it is the only one of the two that ever turns on.
  const canManageRoom = result.can_manage_room === undefined ? null : !!result.can_manage_room;
  if (useMeetingStore.getState().auditoriumCanManageRoom !== canManageRoom) {
    useMeetingStore.setState({ auditoriumCanManageRoom: canManageRoom });
  }
  const currentRole = useMeetingStore.getState().auditoriumRole;
  if (!currentRole || result.role === currentRole) return;
  Logger.debug('[Auditorium] Role changed', currentRole, '→', result.role);
  await rejoinWithNewRole(sessionPid, result.role);
};

const onRoleChangedMessage = () => {
  if (!syncedSessionPid) return;
  // Trust the server, not the message payload: the nudge only says "go look".
  void checkMyRole(syncedSessionPid);
};

/** Start watching for role changes for the auditorium we just joined. Idempotent. */
export const startAuditoriumRoleSync = (sessionPid: string): void => {
  if (syncedSessionPid === sessionPid && pollTimer) return;
  stopAuditoriumRoleSync();
  syncedSessionPid = sessionPid;
  window.addEventListener(AUDITORIUM_ROLE_CHANGED_EVENT, onRoleChangedMessage);
  pollTimer = setInterval(() => {
    if (syncedSessionPid) void checkMyRole(syncedSessionPid);
  }, ROLE_POLL_INTERVAL_MS);
};

/** Stop watching (auditorium left, or the room switched to a regular meeting). */
export const stopAuditoriumRoleSync = (): void => {
  window.removeEventListener(AUDITORIUM_ROLE_CHANGED_EVENT, onRoleChangedMessage);
  clearSpeakerApprovals();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  syncedSessionPid = null;
};
